/**
 * DeviceContext — holds activation state for the player app.
 * Verifies stored credentials on load; exposes login/logout/refresh.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  getStoredActivationKey,
  setStoredActivationKey,
  clearActivation,
  validateDevice,
  playerMe,
  activateDevice,
  getPublicPanelInfo,
  releaseProfileApi,
} from "./playerApi";
import { getDeviceId, resetDeviceId, initDeviceId, isElectron } from "./deviceId";
import { clearActiveProfile, getActiveProfileId } from "./profiles";
import { stopInlineNativeExoPlayer } from "./nativePlayer";

const DeviceContext = createContext(null);

export function DeviceProvider({ children }) {
  const [device, setDevice] = useState(null);
  const [activationKey, setActivationKey] = useState(getStoredActivationKey());
  const [loading, setLoading] = useState(true);
  const [panelName, setPanelName] = useState("ZA Player Pro");
  const [supportWhatsapp, setSupportWhatsapp] = useState(null);
  const [deviceId, setDeviceIdState] = useState(getDeviceId());

  // On mount, initialise MAC (Electron) + validate stored key
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1. Ensure a real MAC is loaded (Electron only — no-op in browser)
        const realId = await initDeviceId();
        if (!cancelled) setDeviceIdState(realId);
      } catch {
        /* ignore */
      }

      try {
        const info = await getPublicPanelInfo();
        if (!cancelled) {
          if (info?.panel_name) setPanelName(info.panel_name);
          if (info?.support_whatsapp !== undefined)
            setSupportWhatsapp(info.support_whatsapp || null);
        }
      } catch {
        /* ignore */
      }

      // 2. In Electron, restore the activation key from the encrypted store
      if (isElectron() && !getStoredActivationKey()) {
        try {
          const k = await window.electronAPI.getCredential("activation_key");
          if (k) {
            setStoredActivationKey(k);
            if (!cancelled) setActivationKey(k);
          }
        } catch {
          /* ignore */
        }
      }

      const key = getStoredActivationKey();
      if (!key) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        await validateDevice();
        const me = await playerMe();
        if (!cancelled) setDevice(me.device);
      } catch (err) {
        // v1.4.1 — REMOVED the previous "silent self-heal" that
        // re-created the device via `activateDevice(key)` on 401.
        //
        // That behaviour turned an admin "Delete Device" into a
        // no-op — the app would resurrect itself silently on the
        // next boot, keep playing streams (helped by the offline
        // IDB playlist cache), and confuse the admin because the
        // deleted device would either "not come back" in the list
        // (until an activation was hit) or would come back with a
        // new UUID at random times.
        //
        // New contract: any 401 / 403 / 404 from `/auth/validate`
        // is treated as "admin explicitly removed this device" →
        // full credential wipe on both localStorage AND Electron's
        // safeStorage → user is returned to the activation screen.
        // If the admin wants the device to keep working, they
        // should NOT delete it (Block is the reversible option).
        //
        // v1.4.20 — also wipe the ACTIVE PROFILE on any revoke
        // path. Before this the deleted-device recovery would
        // land the user on `/player/profiles` after re-activation
        // (correct) but a stale `vupp_active_profile` in
        // localStorage could hop them straight to `/player/menu`
        // for a profile that no longer exists on the new device
        // row → blank grid → user reports "app doesn't come back
        // properly after admin delete". Wiping profiles on revoke
        // guarantees a clean re-activation → profile picker →
        // menu flow.
        const status = err?.response?.status;
        if (!cancelled) {
          if (status === 401 || status === 403 || status === 404) {
            clearActivation();
            clearActiveProfile();
            setActivationKey(null);
            if (isElectron()) {
              try {
                await window.electronAPI.saveCredential("activation_key", null);
              } catch {
                /* ignore */
              }
            }
          } else {
            // Network / server error — original behaviour: clear the
            // stored key so the user lands on the activation screen
            // and can retry. Restoring this rather than "silently
            // keeping" avoids regressing shipped semantics — this
            // patch is scoped strictly to the deleted-device fix.
            clearActivation();
            setActivationKey(null);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activate = useCallback(async (key) => {
    const result = await activateDevice(key);
    setStoredActivationKey(key);
    setActivationKey(key);
    setDevice(result.device);
    // Persist to Electron's encrypted store for next launch
    if (isElectron()) {
      try {
        await window.electronAPI.saveCredential("activation_key", key);
      } catch {
        /* ignore */
      }
    }
    return result.device;
  }, []);

  const logout = useCallback(() => {
    // v1.3.80 — release the profile lease server-side so a re-login
    // (or another device) can claim it immediately instead of
    // waiting out the 5-minute inactivity timeout.
    const pid = getActiveProfileId();
    if (pid) releaseProfileApi(pid).catch(() => {});
    clearActivation();
    clearActiveProfile();
    setActivationKey(null);
    setDevice(null);
    if (isElectron()) {
      try {
        window.electronAPI.saveCredential("activation_key", null);
      } catch {
        /* ignore */
      }
    }
  }, []);

  // v1.4.1 — Live "device revoked" listener.
  //
  // `playerAxios` broadcasts `vupp:device-revoked` when any player
  // API call returns 401/403, which happens the moment the admin
  // deletes/blocks this device. We wipe the stored credentials so
  // the app immediately returns to the activation screen — no need
  // to wait for the next app launch.
  //
  // v1.4.3 — Also kill any running mpv (Electron) or native player
  // (Android) so the currently-playing channel actually STOPS. The
  // prior fix only cleared credentials, but the OS-level player
  // process would keep the provider connection alive until the user
  // manually closed it — which is exactly what the user reported:
  // "devices can still work and not show in devices".
  useEffect(() => {
    const onRevoked = () => {
      const pid = getActiveProfileId();
      if (pid) releaseProfileApi(pid).catch(() => {});
      clearActivation();
      clearActiveProfile();
      setActivationKey(null);
      setDevice(null);
      if (isElectron()) {
        try {
          window.electronAPI.saveCredential("activation_key", null);
        } catch {
          /* ignore */
        }
        // Also stop any mpv / hls.js playback in flight.
        try { window.electronAPI?.stopMpv?.(); } catch { /* ignore */ }
      }
      // Best-effort stop for the native ExoPlayer inline surface
      // AND the fullscreen Activity. Both are no-ops on Web.
      try { stopInlineNativeExoPlayer?.(); } catch { /* ignore */ }
    };
    window.addEventListener("vupp:device-revoked", onRevoked);
    return () => window.removeEventListener("vupp:device-revoked", onRevoked);
  }, []);

  // v1.4.3 — Periodic heartbeat so admin `Delete Device` also kicks
  // in when the app is idle (e.g. user is just watching mpv fullscreen
  // and making NO API calls for minutes at a time).
  //
  // Every 30 s we call `/auth/validate`. It doesn't route through
  // `playerAxios` (uses a plain axios instance), so we must broadcast
  // `vupp:device-revoked` MANUALLY on a 401/403 response.
  useEffect(() => {
    if (!device) return undefined;
    const iv = setInterval(() => {
      validateDevice().catch((err) => {
        const s = err?.response?.status;
        if (s === 401 || s === 403 || s === 404) {
          try {
            window.dispatchEvent(new CustomEvent("vupp:device-revoked", { detail: { status: s, url: "/auth/validate", source: "heartbeat" } }));
          } catch { /* ignore */ }
        }
      });
    }, 30000);
    return () => clearInterval(iv);
  }, [device]);

  // Hard reset = forget device too (forces a new MAC on next launch).
  const factoryReset = useCallback(() => {
    // v1.3.80 — release the profile lease first (best-effort; the
    // reload after 200 ms will detach even if the request is
    // in-flight).
    const pid = getActiveProfileId();
    if (pid) releaseProfileApi(pid).catch(() => {});
    clearActivation();
    clearActiveProfile();
    resetDeviceId();
    setActivationKey(null);
    setDevice(null);
    setTimeout(() => window.location.reload(), 200);
  }, []);

  const value = {
    deviceId,
    activationKey,
    device,
    isActivated: !!device && !!activationKey,
    loading,
    panelName,
    supportWhatsapp,
    activate,
    logout,
    factoryReset,
  };

  return <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>;
}

export function useDevice() {
  const ctx = useContext(DeviceContext);
  if (!ctx) throw new Error("useDevice must be used within DeviceProvider");
  return ctx;
}
